import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { changeLanguage } from "../lib/i18n";

export interface ADHDSettings {
  focusSessionDuration: number; // minutes
  shortBreakDuration: number; // minutes
  longBreakDuration: number; // minutes
  pomodoroSessions: number;
  taskComplexityThreshold: number; // 1-10
  enableNotifications: boolean;
  enableBodyDoubling: boolean;
  timeEstimationMode: "optimistic" | "realistic" | "pessimistic";
}

export interface UISettings {
  theme: "light" | "dark" | "auto";
  fontSize: "small" | "medium" | "large";
  colorScheme: "default" | "high-contrast" | "adhd-friendly";
  animationsEnabled: boolean;
  layoutDensity: "compact" | "comfortable";
  sidebarDefaultOpen: boolean;
}

export interface AudioSettings {
  inputDeviceId?: string;
  outputDeviceId?: string;
  audioQuality: "low" | "medium" | "high";
  noiseSuppression: boolean;
  pushToTalk: boolean;
  autoGainControl: boolean;
}

export interface PrivacySettings {
  saveConversations: boolean;
  dataRetentionDays: number;
  allowAnalytics: boolean;
  exportFormat: "json" | "csv" | "txt";
}

export interface CostSettings {
  dailyBudgetUSD: number;
  weeklyBudgetUSD: number;
  monthlyBudgetUSD: number;
  alertThreshold: number; // percentage
  trackUsage: boolean;
}

export interface UserSettings {
  timezone: string; // e.g., "America/New_York"
  language: string; // e.g., "en", "es", "zh"
}

export interface AppSettings {
  adhd: ADHDSettings;
  ui: UISettings;
  audio: AudioSettings;
  privacy: PrivacySettings;
  cost: CostSettings;
  user: UserSettings;
}

const defaultSettings: AppSettings = {
  adhd: {
    focusSessionDuration: 25,
    shortBreakDuration: 5,
    longBreakDuration: 15,
    pomodoroSessions: 4,
    taskComplexityThreshold: 5,
    enableNotifications: true,
    enableBodyDoubling: false,
    timeEstimationMode: "realistic",
  },
  ui: {
    theme: "dark",
    fontSize: "medium",
    colorScheme: "adhd-friendly",
    animationsEnabled: true,
    layoutDensity: "comfortable",
    sidebarDefaultOpen: true,
  },
  audio: {
    audioQuality: "medium",
    noiseSuppression: true,
    pushToTalk: false,
    autoGainControl: true,
  },
  privacy: {
    saveConversations: true,
    dataRetentionDays: 30,
    allowAnalytics: false,
    exportFormat: "json",
  },
  cost: {
    dailyBudgetUSD: 5,
    weeklyBudgetUSD: 25,
    monthlyBudgetUSD: 100,
    alertThreshold: 80,
    trackUsage: true,
  },
  user: {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Auto-detect user's timezone
    language: "en", // Default to English, will be auto-detected by i18n
  },
};

interface SettingsContextType {
  settings: AppSettings;
  updateSettings: (category: keyof AppSettings, updates: Partial<any>) => void;
  resetSettings: (category?: keyof AppSettings) => void;
  exportSettings: () => string;
  importSettings: (settingsJson: string) => boolean;
  changeLanguage: (languageCode: string) => Promise<void>;
}

export const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
};

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<AppSettings>(() => {
    const saved = localStorage.getItem("flowcus-settings");
    if (saved) {
      try {
        return { ...defaultSettings, ...JSON.parse(saved) };
      } catch (error) {
        return defaultSettings;
      }
    }
    return defaultSettings;
  });

  // Apply theme to document
  useEffect(() => {
    const applyTheme = () => {
      let theme = settings.ui.theme;

      // Handle 'auto' theme based on system preference
      if (theme === "auto") {
        const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
        theme = prefersDark ? "dark" : "light";
      }

      // Apply theme to document
      document.documentElement.setAttribute("data-theme", theme);

      // Also add/remove dark class for backward compatibility
      if (theme === "dark") {
        document.body.classList.add("dark");
      } else {
        document.body.classList.remove("dark");
      }
    };

    applyTheme();

    // Listen for system theme changes when in auto mode
    if (settings.ui.theme === "auto") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = () => applyTheme();

      // Check if addEventListener is supported
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener("change", handleChange);
        return () => mediaQuery.removeEventListener("change", handleChange);
      }
    }
  }, [settings.ui.theme]);

  useEffect(() => {
    localStorage.setItem("flowcus-settings", JSON.stringify(settings));
  }, [settings]);

  const updateSettings = (category: keyof AppSettings, updates: Partial<any>) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        ...updates,
      },
    }));
  };

  const resetSettings = (category?: keyof AppSettings) => {
    if (category) {
      setSettings(prev => ({
        ...prev,
        [category]: defaultSettings[category],
      }));
    } else {
      setSettings(defaultSettings);
    }
  };

  const exportSettings = () => {
    return JSON.stringify(settings, null, 2);
  };

  const importSettings = (settingsJson: string): boolean => {
    try {
      const imported = JSON.parse(settingsJson);
      setSettings({ ...defaultSettings, ...imported });
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleLanguageChange = async (languageCode: string): Promise<void> => {
    try {
      await changeLanguage(languageCode);
      updateSettings("user", { language: languageCode });
    } catch (error) {
      throw error;
    }
  };

  return (
    <SettingsContext.Provider
      value={{
        settings,
        updateSettings,
        resetSettings,
        exportSettings,
        importSettings,
        changeLanguage: handleLanguageChange,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};
